import React, { useMemo } from 'react';
import { Polygon } from 'react-leaflet';
import { cellToBoundary } from 'h3-js';

export interface GridCell {
    cell: string;
    center: { lat: number; lng: number };
    clusterId: string | null;
    clusterName: string | null;
}

interface H3GridOverlayProps {
    grid: GridCell[];
    selectedCells: Set<string>;
    onCellClick?: (cell: string, clusterId: string | null) => void;
    mode?: 'view' | 'select';
    targetClusterId?: string | null;
}

const CLAIMED_COLOR = '#22c55e';
const UNCLAIMED_COLOR = 'rgba(128,128,128,0.3)';
const SELECTED_COLOR = '#3b82f6';
const TARGET_CLUSTER_COLOR = '#93c5fd';

const H3GridOverlay: React.FC<H3GridOverlayProps> = ({
    grid,
    selectedCells,
    onCellClick,
    mode = 'view',
    targetClusterId = null
}) => {
    const polygons = useMemo(() => {
        return grid.map(({ cell, clusterId }) => {
            const boundary = cellToBoundary(cell);
            const positions: [number, number][] = boundary.map(([lat, lng]) => [lat, lng]);
            const isSelected = selectedCells.has(cell);
            const isClaimed = !!clusterId;
            const isTargetCluster = !!targetClusterId && clusterId === targetClusterId;

            let fillColor = UNCLAIMED_COLOR;
            let color = 'rgba(100,100,100,0.5)';
            if (isSelected) {
                fillColor = SELECTED_COLOR;
                color = SELECTED_COLOR;
            } else if (isTargetCluster) {
                fillColor = TARGET_CLUSTER_COLOR;
                color = TARGET_CLUSTER_COLOR;
            } else if (isClaimed) {
                fillColor = CLAIMED_COLOR;
                color = CLAIMED_COLOR;
            }

            return {
                key: cell,
                cell,
                positions,
                fillColor,
                color,
                clusterId
            };
        });
    }, [grid, selectedCells, targetClusterId]);

    return (
        <>
            {polygons.map(({ key, positions, fillColor, color, cell, clusterId }) => (
                <Polygon
                    key={key}
                    positions={positions}
                    pathOptions={{
                        color,
                        fillColor,
                        fillOpacity: 0.4,
                        weight: 1,
                        opacity: 0.8
                    }}
                    eventHandlers={{
                        click: () => {
                            if (mode === 'select' && onCellClick) {
                                onCellClick(cell, clusterId);
                            }
                        }
                    }}
                />
            ))}
        </>
    );
};

export default H3GridOverlay;
